<template>
  <v-container fluid class="pa-6">
    <page-bar title="Profile Manager" subtitle="Manage profiles">
      <template v-slot:actions>
        <v-btn small color="green" dark @click="openCreateDialog">
          New Profile +
        </v-btn>
      </template>
    </page-bar>

    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-col class="pt-0">
            <v-row>
              <v-card color="primaryDark" width="100%" dark class="pa-2 subtitle-1">
                <v-row justify="start">
                  <v-col class="pl-4">Profiles</v-col>
                  <v-col>
                    <v-text-field
                      dense
                      hide-details
                      label="Search"
                      v-model="search"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card>
            </v-row>

            <v-data-table
              :headers="headers"
              :items="marketProfiles"
              :search="search"
              :loading="loading"
              class="mt-2"
            >
              <template v-slot:item.actions="{ item }">
                <v-btn icon small @click="openEditDialog(item)">
                  <v-icon size="20">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small @click="openDeleteDialog(item)">
                  <v-icon size="20">mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-col>
        </v-card>
      </v-col>
    </v-row>

    <!-- Import Profile Values -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="subtitle-1 font-weight-medium">
                <v-icon left>mdi-upload</v-icon> Import Profile Values
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2">
                <v-col cols="12" md="8">
                  <v-file-input
                    dense
                    hide-details
                    outlined
                    label="Select .xlsx file"
                    accept=".xlsx"
                    v-model="importFile"
                    prepend-icon="mdi-microsoft-excel"
                  ></v-file-input>
                </v-col>
                <v-col cols="12" md="3" class="d-flex align-center">
                  <v-btn
                    color="green"
                    dark
                    :disabled="!importFile || !selectedMarketId || importing"
                    :loading="importing"
                    @click="runImport"
                  >
                    <v-icon left>mdi-upload</v-icon> Import
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Import Results -->
              <div v-if="importResult" class="mt-4">
                  <!-- File-level errors (e.g. missing columns, no product section) -->
                  <v-alert
                    v-if="importResult.errors && importResult.errors.length"
                    type="error"
                    dense
                    class="mb-2"
                  >
                    <div v-for="(err, i) in importResult.errors" :key="i">{{ err }}</div>
                  </v-alert>

                  <!-- Summary counts -->
                  <v-alert
                    :type="importResult.errorCount > 0 ? 'error' : importResult.actionableWarnings.length > 0 ? 'warning' : 'success'"
                    dense
                    class="mb-2"
                  >
                    <strong>Import complete.</strong>
                    Processed <strong>{{ importResult.totalRows }}</strong> row(s) &mdash;
                    <strong>{{ importResult.successCount }}</strong> updated,
                    <span v-if="importResult.noValuesCount > 0">
                      <strong>{{ importResult.noValuesCount }}</strong> skipped (no values),
                    </span>
                    <strong>{{ importResult.actionableWarnings.length }}</strong> warning(s),
                    <strong>{{ importResult.errorCount }}</strong> error(s).
                  </v-alert>

                  <!-- Actionable warnings (real issues, not "no values" skips) -->
                  <v-card v-if="importResult.actionableWarnings.length" outlined class="mb-2">
                    <v-card-subtitle class="pb-1 orange--text darken-3">
                      <v-icon color="orange darken-2" small left>mdi-alert</v-icon>
                      Warnings ({{ importResult.actionableWarnings.length }})
                    </v-card-subtitle>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th style="width:60px">Row</th>
                          <th>Asset</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in importResult.actionableWarnings" :key="row.rowNumber">
                          <td>{{ row.rowNumber }}</td>
                          <td>{{ row.identifier || row.assetDetailId || row.profileId }}</td>
                          <td>{{ row.message }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card>

                  <!-- Errors -->
                  <v-card v-if="importResult.errorCount > 0" outlined class="mb-2">
                    <v-card-subtitle class="pb-1 red--text">
                      <v-icon color="red" small left>mdi-close-circle</v-icon>
                      Errors ({{ importResult.errorCount }})
                    </v-card-subtitle>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th style="width:60px">Row</th>
                          <th>Asset</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in importResult.rowResults.filter(r => r.status === 'error')" :key="row.rowNumber">
                          <td>{{ row.rowNumber }}</td>
                          <td>{{ row.identifier || row.assetDetailId || row.profileId }}</td>
                          <td>{{ row.message }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <!-- Export Profile Values -->
    <v-row class="mt-3">
      <v-col cols="12">
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="subtitle-1 font-weight-medium">
                <v-icon left>mdi-download</v-icon> Export Profile Values
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2">
                <v-col cols="12" md="8">
                  <v-select
                    dense
                    hide-details
                    outlined
                    clearable
                    label="Profile (optional — leave blank for all)"
                    v-model="exportProfileId"
                    :items="profileItems"
                    item-text="text"
                    item-value="value"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-btn
                    color="primary"
                    dark
                    :disabled="!selectedMarketId || exporting"
                    :loading="exporting"
                    @click="runExport"
                  >
                    <v-icon left>mdi-download</v-icon> Export
                  </v-btn>
                </v-col>
              </v-row>
              <div class="caption grey--text mt-2">
                Exports all Product section assets for the current market.
                Only the <em>Identifier</em> field is included.
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="profileDialog" width="500px" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ editingProfile ? 'Edit Profile' : 'Create Profile' }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            label="Name"
            v-model="profileForm.name"
            :rules="[rules.required]"
          ></v-text-field>
          <v-textarea
            label="Description"
            v-model="profileForm.description"
            auto-grow
            rows="3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" dark @click="closeDialog">Cancel</v-btn>
          <v-btn color="green darken-1" dark @click="saveProfile" :disabled="!profileForm.name || !profileForm.name.trim() || (!editingProfile && !selectedMarketId)">
            {{ editingProfile ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" width="500px" persistent>
      <v-card>
        <v-card-title class="pa-3 headline">Delete Profile</v-card-title>
        <v-card-text class="subtitle-1">
          Are you sure you want to delete profile
          <b>{{ profileToDelete ? profileToDelete.name : '' }}</b>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" dark @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="red darken-1" dark @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import CONFIG from '../../../appconfig.json'
import PageBar from '../../shared/PageBar'
import { mapGetters, mapState, mapActions } from 'vuex'

export default {
  name: 'Profiles',
  components: { PageBar },
  data: () => ({
    profiles: [],
    loading: false,
    search: '',
    headers: [
      { text: 'Name', value: 'name' },
      { text: 'Description', value: 'description' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
    ],
    profileDialog: false,
    deleteDialog: false,
    editingProfile: null,
    profileToDelete: null,
    profileForm: {
      name: '',
      description: '',
    },
    rules: {
      required: (v) => !!v || 'Required.',
    },
    // Import
    importFile: null,
    importing: false,
    importResult: null,
    // Export
    exportProfileId: null,
    exporting: false,
  }),
  computed: {
    ...mapState('assetxpstate', ['selectedMarketId']),
    marketProfiles() {
      const selectedMarket = (this.selectedMarketId || '').trim().toLowerCase()
      return (this.profiles || []).filter((p) => {
        if (!p) return false

        const profileMarket = (p.marketId || '').trim().toLowerCase()
        if (!selectedMarket) return true

        // Keep legacy profiles visible in manager during migration.
        return profileMarket === selectedMarket || profileMarket === '' || profileMarket === 'global'
      })
    },
    profileItems() {
      return this.marketProfiles
        .slice()
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
        .map((p) => ({ text: p.name, value: p.id }))
    },
  },
  mounted() {
    this.loadProfiles()
  },
  methods: {
    ...mapGetters('authentication', ['GetAuthHeaderObj']),
    ...mapActions('assetxpstate', ['ImportProfileValues', 'ExportProfileValues']),
    async loadProfiles() {
      this.loading = true
      try {
        const response = await axios.get(
          CONFIG.platformEndpoints.assetxpapi + 'profile/getall',
          this.GetAuthHeaderObj()
        )
        this.profiles = response.data
      } catch (error) {
        console.error('Failed to load profiles', error)
      } finally {
        this.loading = false
      }
    },
    async runImport() {
      if (!this.importFile || !this.selectedMarketId) return
      this.importing = true
      this.importResult = null
      try {
        const result = await this.$store.dispatch('assetxpstate/ImportProfileValues', {
          marketId: this.selectedMarketId,
          file: this.importFile,
        })
        this.importResult = this.normalizeImportResult(result)
        this.importFile = null
      } catch (error) {
        console.error('Import failed', error)
        this.importResult = {
          totalRows: 0,
          successCount: 0,
          noValuesCount: 0,
          actionableWarnings: [],
          warningCount: 0,
          errorCount: 0,
          errors: ['Import request failed. Please check the file and try again.'],
          rowResults: [],
        }
      } finally {
        this.importing = false
      }
    },
    normalizeImportResult(result) {
      const rowResults = (result.rowResults || []).map((row) => {
        const normalizedRow = { ...row }
        if (this.isNoValuesWarning(normalizedRow.message)) {
          normalizedRow.status = 'warning'
          normalizedRow._noValues = true
        }

        return normalizedRow
      })

      const noValuesRows = rowResults.filter((row) => row._noValues)
      const actionableWarnings = rowResults.filter((row) => row.status === 'warning' && !row._noValues)

      return {
        ...result,
        rowResults,
        successCount: rowResults.filter((row) => row.status === 'success').length,
        noValuesCount: noValuesRows.length,
        actionableWarnings,
        warningCount: rowResults.filter((row) => row.status === 'warning').length,
        errorCount: rowResults.filter((row) => row.status === 'error').length,
      }
    },
    isNoValuesWarning(message) {
      return typeof message === 'string'
        && message.startsWith('No values provided (Value-1 through Value-9 are all empty).')
    },
    async runExport() {
      if (!this.selectedMarketId) return
      this.exporting = true
      try {
        const profileName = this.exportProfileId
          ? (this.profiles || []).find((profile) => profile.id === this.exportProfileId)?.name || this.exportProfileId
          : 'AllProfiles'
        await this.$store.dispatch('assetxpstate/ExportProfileValues', {
          marketId: this.selectedMarketId,
          profileId: this.exportProfileId || null,
          profileName,
        })
      } catch (error) {
        console.error('Export failed', error)
      } finally {
        this.exporting = false
      }
    },
    openCreateDialog() {
      this.editingProfile = null
      this.profileForm = { name: '', description: '' }
      this.profileDialog = true
    },
    openEditDialog(profile) {
      this.editingProfile = profile
      this.profileForm = { name: profile.name, description: profile.description || '' }
      this.profileDialog = true
    },
    closeDialog() {
      this.profileDialog = false
      this.editingProfile = null
    },
    async saveProfile() {
      try {
        if (this.editingProfile) {
          await axios.post(
            CONFIG.platformEndpoints.assetxpapi + 'profile/update',
            { ...this.editingProfile, name: this.profileForm.name, description: this.profileForm.description },
            this.GetAuthHeaderObj()
          )
        } else {
          await axios.post(
            CONFIG.platformEndpoints.assetxpapi + 'profile/create',
            {
              name: this.profileForm.name,
              description: this.profileForm.description,
              marketId: this.selectedMarketId,
            },
            this.GetAuthHeaderObj()
          )
        }

        this.closeDialog()
        await this.loadProfiles()
      } catch (error) {
        console.error('Failed to save profile', error)
      }
    },
    openDeleteDialog(profile) {
      this.profileToDelete = profile
      this.deleteDialog = true
    },
    async confirmDelete() {
      try {
        await axios.delete(
          CONFIG.platformEndpoints.assetxpapi + 'profile/delete/' + this.profileToDelete.id,
          this.GetAuthHeaderObj()
        )
        this.deleteDialog = false
        this.profileToDelete = null
        await this.loadProfiles()
      } catch (error) {
        console.error('Failed to delete profile', error)
      }
    },
  },
}
</script>
